import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Phone, PhoneOff, PhoneIncoming } from 'lucide-react';
import { Call } from '@twilio/voice-sdk';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import divusLogoWhite from '@/assets/divus-logo-white.png';

interface IncomingCallModalProps {
  call: Call | null;
  onAccept: (call: Call) => void;
  onReject: (call: Call) => void;
}

export default function IncomingCallModal({ call, onAccept, onReject }: IncomingCallModalProps) {
  const isMobile = useIsMobile();
  const [callerInfo, setCallerInfo] = useState<{
    name: string;
    phone: string;
    initials: string;
  } | null>(null);
  const [isRinging, setIsRinging] = useState(true);

  // Vibration for incoming calls on mobile
  useEffect(() => {
    if (!call) return;

    let vibrateInterval: NodeJS.Timeout | null = null;

    if ('vibrate' in navigator) {
      // Vibrate pattern: vibrate 200ms, pause 100ms, vibrate 200ms, pause 100ms, vibrate 200ms
      const vibratePattern = () => {
        try {
          navigator.vibrate([200, 100, 200, 100, 200]);
        } catch (e) {
          console.log('Vibration not supported');
        }
      };

      vibratePattern();
      vibrateInterval = setInterval(vibratePattern, 2000);
    }

    return () => {
      if (vibrateInterval) clearInterval(vibrateInterval);
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate(0); // Stop vibration
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [call]);

  useEffect(() => {
    if (!call) {
      setCallerInfo(null);
      setIsRinging(true);
      return;
    }

    // Extract caller number
    const fromNumber = call.parameters.From;
    
    // Fetch contact information
    const fetchCallerInfo = async () => {
      try {
        const { data: contact } = await supabase
          .from('contacts')
          .select('first_name, last_name, primary_phone')
          .eq('primary_phone', fromNumber)
          .maybeSingle();

        if (contact) {
          setCallerInfo({
            name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact',
            phone: fromNumber,
            initials: `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase() || 'C'
          });
        } else {
          setCallerInfo({
            name: 'Unknown Number',
            phone: fromNumber,
            initials: '?'
          });
        }
      } catch (error) {
        console.error('Error fetching caller info:', error);
        setCallerInfo({
          name: 'Unknown Number',
          phone: fromNumber,
          initials: '?'
        });
      }
    };

    fetchCallerInfo();

    // Simulate ringing animation
    const ringInterval = setInterval(() => {
      setIsRinging(prev => !prev);
    }, 1000);

    return () => clearInterval(ringInterval);
  }, [call]);

  const handleAccept = () => {
    if (call) {
      onAccept(call);
    }
  };

  const handleReject = () => {
    if (call) {
      onReject(call);
    }
  };

  if (!call || !callerInfo) return null;

  // Mobile fullscreen version
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-between" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Logo at top */}
        <div className="pt-6">
          <img src={divusLogoWhite} alt="Divus Legal Group" className="h-8 w-auto opacity-90" />
        </div>
        
        {/* Top section with caller info */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Animated rings behind avatar */}
          <div className="relative">
            <div className={`absolute inset-0 rounded-full bg-primary/20 scale-150 ${isRinging ? 'animate-ping' : ''}`} style={{ animationDuration: '1.5s' }} />
            <div className={`absolute inset-0 rounded-full bg-primary/10 scale-125 ${isRinging ? 'animate-pulse' : ''}`} />
            
            <Avatar className={`w-32 h-32 border-4 border-white/20 shadow-2xl transition-transform duration-300 ${isRinging ? 'scale-110' : 'scale-100'}`}>
              <AvatarFallback className="text-4xl bg-gradient-to-br from-primary to-primary/80 text-white font-bold">
                {callerInfo.initials}
              </AvatarFallback>
            </Avatar>
            
            <div className={`absolute -top-2 -right-2 p-3 bg-primary rounded-full shadow-lg ${isRinging ? 'animate-bounce' : ''}`}>
              <PhoneIncoming className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Caller information */}
          <div className="text-center mt-8 space-y-2">
            <h2 className="text-3xl font-bold text-white">{callerInfo.name}</h2>
            <p className="text-lg text-white/70">{callerInfo.phone}</p>
          </div>

          {/* Incoming call indicator */}
          <div className="mt-6 flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            <p className="text-white font-medium">Incoming Call</p>
          </div>
        </div>

        {/* Bottom action buttons */}
        <div className="w-full px-8 pb-8 pt-8">
          <div className="flex items-center justify-center gap-12">
            {/* Reject button */}
            <div className="flex flex-col items-center gap-3">
              <Button
                variant="destructive"
                size="icon"
                onClick={handleReject}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-2xl ring-4 ring-red-500/30 transition-all active:scale-95"
              >
                <PhoneOff className="w-8 h-8" />
              </Button>
              <span className="text-white/80 text-sm font-medium">Decline</span>
            </div>

            {/* Accept button */}
            <div className="flex flex-col items-center gap-3">
              <Button
                size="icon"
                onClick={handleAccept}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-2xl ring-4 ring-green-500/30 transition-all active:scale-95"
              >
                <Phone className="w-8 h-8" />
              </Button>
              <span className="text-white/80 text-sm font-medium">Answer</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop dialog version
  return (
    <Dialog open={!!call} onOpenChange={() => handleReject()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Incoming Call</DialogTitle>
          <DialogDescription className="text-center">
            {callerInfo.name} is calling you
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center gap-6 py-8">
          {/* Caller avatar with animation */}
          <div className={`relative transition-transform ${isRinging ? 'scale-110' : 'scale-100'}`}>
            <Avatar className="w-24 h-24 border-4 border-primary/20">
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                {callerInfo.initials}
              </AvatarFallback>
            </Avatar>
            <div className={`absolute -top-1 -right-1 p-2 bg-primary rounded-full ${isRinging ? 'animate-pulse' : ''}`}>
              <PhoneIncoming className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* Caller information */}
          <div className="text-center space-y-1">
            <h3 className="text-xl font-semibold text-foreground">{callerInfo.name}</h3>
            <p className="text-sm text-muted-foreground">{callerInfo.phone}</p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-4 w-full">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleReject}
              className="flex-1 gap-2"
            >
              <PhoneOff className="w-5 h-5" />
              Reject
            </Button>
            <Button
              size="lg"
              onClick={handleAccept}
              className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Phone className="w-5 h-5" />
              Answer
            </Button>
          </div>
        </div>

        {/* Call indicator */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground animate-pulse">
            {isRinging ? '📞 Ringing...' : ''}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
